import { createToaster } from '@meforma/vue-toaster';
import { downloadBlobResponse } from '@/backend/download';
import { exportPureOrders, fetchPureCityOptions, fetchPureOrders } from '@/backend/pureBiddingApi';
import { getWinnerValue } from '@/pages/pureBidding/helpers';

const toaster = createToaster();

const FILTERS_VERSION = 1;
const FILTERS_STORAGE_KEY = 'pureBiddingFilters';
const PAGE_LIMIT = 100;

const DEFAULT_SORTS = [
  { field: 'context.status', dir: 1 },
  { field: 'context.create_dt', dir: -1 },
];

const TODAY = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed.filtersVersion !== FILTERS_VERSION) return {};
    if (!Array.isArray(parsed.status)) parsed.status = [];
    if (!Array.isArray(parsed.source)) parsed.source = [];
    if (!Array.isArray(parsed.fromPoints)) parsed.fromPoints = [];
    if (!Array.isArray(parsed.toPoints)) parsed.toPoints = [];
    if (!Array.isArray(parsed.sorts)) parsed.sorts = DEFAULT_SORTS;
    if (typeof parsed.winner !== 'string') parsed.winner = '';
    if (typeof parsed.page !== 'number') parsed.page = 1;
    return parsed;
  } catch {
    return {};
  }
}

function persistFilters(state) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
      trip_id: state.trip_id,
      source: state.source,
      fromPoints: state.fromPoints,
      toPoints: state.toPoints,
      status: state.status,
      winner: state.winner,
      vehicleType: state.vehicleType,
      hazardous: state.hazardous,
      tasked: state.tasked,
      sorts: state.sorts,
      loadingDateFrom: state.loadingDateFrom,
      loadingDateTo: state.loadingDateTo,
      page: state.page,
      highlightMode: state.highlightMode,
      highlightPalette: state.highlightPalette,
      filtersVersion: FILTERS_VERSION,
    }));
  } catch {
    // ignore
  }
}

const getDefaultState = () => ({
  trip_id: '',
  source: [],
  status: [],
  winner: '',
  vehicleType: '',
  hazardous: false,
  tasked: '',
  fromPoints: [],
  toPoints: [],
  sorts: DEFAULT_SORTS,
  loadingDateFrom: '',
  loadingDateTo: '',
  highlightMode: 'none',
  highlightPalette: 'colorful',
  page: 1,
  // data
  orders: [],
  totalItems: 0,
  totalPages: 1,
  loading: false,
  exportLoading: false,
});

function toRfc3339(dateStr, endOfDay = false) {
  if (!dateStr) return undefined;
  return endOfDay ? `${dateStr}T23:59:59Z` : `${dateStr}T00:00:00Z`;
}

export function buildPureBiddingParams(state, { includePaging = true } = {}) {
  const sorts = Array.isArray(state.sorts) && state.sorts.length ? state.sorts : DEFAULT_SORTS;
  const firstSort = sorts[0];
  const params = {
    trip_id: state.trip_id || undefined,
    source: state.source.length > 0 ? state.source.join(',') : undefined,
    status: state.status.length > 0 ? state.status : state.winner ? ['WON'] : undefined,
    vehicle_type: state.vehicleType || undefined,
    hazardous: state.hazardous || undefined,
    tasked: state.tasked || undefined,
    from_points: state.fromPoints.length > 0 ? state.fromPoints : undefined,
    to_points: state.toPoints.length > 0 ? state.toPoints : undefined,
    create_dt_from: toRfc3339(state.loadingDateFrom),
    create_dt_to: toRfc3339(state.loadingDateTo, true),
    sort_by: firstSort?.field,
    sort_dir: firstSort?.dir,
  };
  if (includePaging) {
    params.page = state.page;
    params.limit = PAGE_LIMIT;
  } else {
    params.page = 1;
    params.limit = 10000;
  }
  return params;
}

function applyWinnerFilter(data, state) {
  if (!state.winner) return data;

  const items = Array.isArray(data?.items) ? data.items : [];
  const filtered = items.filter((order) => getWinnerValue(order) === state.winner);
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const page = Math.min(Math.max(Number(state.page) || 1, 1), pages);
  const start = (page - 1) * PAGE_LIMIT;

  return {
    ...data,
    items: filtered.slice(start, start + PAGE_LIMIT),
    meta: {
      ...(data?.meta || {}),
      page,
      limit: PAGE_LIMIT,
      total,
      pages,
    },
  };
}

const namespaced = true;

const state = (() => {
  const stored = loadStoredFilters();
  return { ...getDefaultState(), ...stored };
})();

const mutations = {
  SET_TRIP_ID(state, value) { state.trip_id = value; state.page = 1; },
  SET_SOURCE(state, value) { state.source = value; state.page = 1; },
  SET_STATUS(state, value) { state.status = value; state.page = 1; },
  SET_WINNER(state, value) { state.winner = value; state.page = 1; },
  SET_VEHICLE_TYPE(state, value) { state.vehicleType = value; state.page = 1; },
  SET_HAZARDOUS(state, value) { state.hazardous = value; state.page = 1; },
  SET_TASKED(state, value) { state.tasked = value; state.page = 1; },
  SET_FROM_POINTS(state, value) { state.fromPoints = value; state.page = 1; },
  SET_TO_POINTS(state, value) { state.toPoints = value; state.page = 1; },
  SET_SORTS(state, value) { state.sorts = value.length > 0 ? value : DEFAULT_SORTS; state.page = 1; },
  SET_LOADING_DATE_FROM(state, value) { state.loadingDateFrom = value; state.page = 1; },
  SET_LOADING_DATE_TO(state, value) { state.loadingDateTo = value; state.page = 1; },
  SET_LOADING_DATE_RANGE(state, { from, to }) {
    state.loadingDateFrom = from;
    state.loadingDateTo = to;
    state.page = 1;
  },
  SET_PAGE(state, value) { state.page = value; },
  SET_HIGHLIGHT_MODE(state, value) { state.highlightMode = value; },
  SET_HIGHLIGHT_PALETTE(state, value) { state.highlightPalette = value; },
  SET_ORDERS(state, data) {
    state.orders = Array.isArray(data?.items) ? data.items : [];
    const meta = data?.meta;
    state.totalItems = Number.isFinite(meta?.total) ? meta.total : 0;
    state.totalPages = Number.isFinite(meta?.pages) ? meta.pages : 1;
  },
  SET_LOADING(state, value) { state.loading = value; },
  SET_EXPORT_LOADING(state, value) { state.exportLoading = value; },
  CLEAR_FILTERS(state) {
    state.trip_id = '';
    state.source = [];
    state.status = [];
    state.winner = '';
    state.vehicleType = '';
    state.hazardous = false;
    state.tasked = '';
    state.fromPoints = [];
    state.toPoints = [];
    state.sorts = DEFAULT_SORTS;
    state.loadingDateFrom = '';
    state.loadingDateTo = '';
  },
};

const getters = {
  isFilterModified(state) {
    return state.trip_id
      || state.source.length
      || state.status.length
      || state.winner
      || state.vehicleType
      || state.hazardous
      || state.tasked
      || state.fromPoints.length
      || state.toPoints.length
      || state.loadingDateFrom
      || state.loadingDateTo;
  },
};

const actions = {
  async getOrders({ commit, state }) {
    commit('SET_LOADING', true);
    try {
      const data = await fetchPureOrders(buildPureBiddingParams(state, { includePaging: !state.winner }));
      commit('SET_ORDERS', applyWinnerFilter(data, state));
    } catch (e) {
      console.error('PureBidding fetch error:', e);
      throw e;
    } finally {
      commit('SET_LOADING', false);
      persistFilters(state);
    }
  },

  async exportToExcel({ commit, state }) {
    if (state.exportLoading) return;
    commit('SET_EXPORT_LOADING', true);
    try {
      const response = await exportPureOrders(buildPureBiddingParams(state, { includePaging: false }));
      await downloadBlobResponse(response, 'pure_bidding.xlsx');
    } catch (e) {
      console.error('PureBidding export error:', e);
      toaster.error(e?.message || 'Не удалось выгрузить торги в Excel');
    } finally {
      commit('SET_EXPORT_LOADING', false);
    }
  },

  async getCityOptions({ state }, { kind, q }) {
    try {
      const data = await fetchPureCityOptions({
        kind,
        q,
        limit: 30,
        status: state.status.length > 0 ? state.status : undefined,
        source: state.source.length > 0 ? state.source.join(',') : undefined,
        hazardous: state.hazardous || undefined,
        tasked: state.tasked || undefined,
        vehicle_type: state.vehicleType || undefined,
        create_dt_from: toRfc3339(state.loadingDateFrom),
        create_dt_to: toRfc3339(state.loadingDateTo, true),
      });
      return data.items || [];
    } catch (e) {
      console.error('PureBidding city options error:', e);
      return [];
    }
  },

  setTripId({ commit, dispatch }, value) { commit('SET_TRIP_ID', value); dispatch('getOrders'); },
  setSource({ commit, dispatch }, value) { commit('SET_SOURCE', value); dispatch('getOrders'); },
  setStatus({ commit, dispatch }, value) { commit('SET_STATUS', value); dispatch('getOrders'); },
  setWinner({ commit, dispatch }, value) { commit('SET_WINNER', value); dispatch('getOrders'); },
  setVehicleType({ commit, dispatch }, value) { commit('SET_VEHICLE_TYPE', value); dispatch('getOrders'); },
  setHazardous({ commit, dispatch }, value) { commit('SET_HAZARDOUS', value); dispatch('getOrders'); },
  setTasked({ commit, dispatch }, value) { commit('SET_TASKED', value); dispatch('getOrders'); },
  setFromPoints({ commit, dispatch }, value) { commit('SET_FROM_POINTS', value); dispatch('getOrders'); },
  setToPoints({ commit, dispatch }, value) { commit('SET_TO_POINTS', value); dispatch('getOrders'); },
  setSorts({ commit, dispatch }, value) { commit('SET_SORTS', value); dispatch('getOrders'); },
  setPage({ commit, dispatch }, value) { commit('SET_PAGE', value); dispatch('getOrders'); },
  setLoadingDateFrom({ commit, dispatch }, value) { commit('SET_LOADING_DATE_FROM', value); dispatch('getOrders'); },
  setLoadingDateTo({ commit, dispatch }, value) { commit('SET_LOADING_DATE_TO', value); dispatch('getOrders'); },
  setLoadingDateRange({ commit, dispatch }, value) { commit('SET_LOADING_DATE_RANGE', value); dispatch('getOrders'); },
  setHighlightMode({ commit }, value) { commit('SET_HIGHLIGHT_MODE', value); },
  setHighlightPalette({ commit }, value) { commit('SET_HIGHLIGHT_PALETTE', value); },

  clearFilters({ commit, dispatch }) {
    commit('CLEAR_FILTERS');
    dispatch('getOrders');
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
  namespaced,
};
